// src/pages/NewIssuePage.tsx — SCE Issues
// Formulaire de déclaration d'un nouveau problème (le plus critique — <60s pour un opérateur).
//
// ─── Lot 3.5 — Simplification ───
//
// La Famille et la Criticité ne sont plus saisies par le déclarant : à la
// création, l'API les force à 'À qualifier'. Le coordinateur métier les
// renseigne depuis la fiche détail.
//
// ─── Pièces jointes (multi-fichiers) ───
//
// L'opérateur peut ajouter plusieurs fichiers (photos, PDF, vidéos).
// À la soumission :
//   1. createIssue() → issue créée dans SP
//   2. envoyerFichiers(issue.id, fichiers) → upload en séquence (versions annotées comprises) vers SCE_PiecesJointes/P2026-NNNN/
//   3. Si certains uploads échouent → snackbar warning (mais la déclaration est validée)
//
// PJ permanentes : avertissement affiché dans le picker avant soumission.
//
// ─── Plusieurs machines (Lot 14, option C) ───
//
// « Même défaut sur d'autres machines ? » : une fiche PAR machine, reliées par
// la colonne Groupe. La première ouvre le groupe (son ID), reçoit les pièces
// jointes — visibles depuis les fiches sœurs —, puis une fiche par autre
// machine, même titre, même description. Le flow 1 n'alerte qu'une fois, sur
// la première (cf. docs/flows/flow-1-alerte-coordinateurs.md).

import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import type { EtatDeclarationNcs } from '@/components/layout/ChoixDeclaration'
import type { EtatModale } from '@/components/layout/ModaleRoute'
import { lienProbleme } from '@/utils/liensFiches'
import { X, Search, Info as InfoIcon, Plus } from 'lucide-react'
import { useRetour } from '@/hooks/useRetour'
import { AppShell } from '@/components/layout/AppShell'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/Button'
import { useSnackbar } from '@/components/ui/Snackbar'
import { useIssues } from '@/hooks/useIssues'
import { useConfirmerEcriture } from '@/features/vue-role/contexte'
import {
  METIERS_COULEURS,
  METIERS_COULEURS_TEXTE,
  METIERS_LABELS_COURT,
  METIERS_ORDRE,
} from '@/config/metiers.config'
import { SUJETS_CONFIG, rangeeMetiersEtSujets, sujetsOuverts } from '@/config/sujets.config'
import { estAdmin } from '@/config/admin.config'
import {
  deduireCategorieEquipement,
  getEquipementByNumeroSerie,
  searchEquipements,
} from '@/api/equipements.api'
import { envoyerFichiers } from '@/api/files.api'
import { suivreIssueParId } from '@/api/issues.api'
import { notifierMentions } from '@/api/mentions.api'
import { resumeMentions } from '@/utils/mentions'
import { ZoneMentions } from '@/components/mentions/ZoneMentions'
import { lireReglageAbonnementsDe } from '@/api/abonnements.api'
import { PieceJointePicker, preparerFichiers, type FichierEnAttente } from '@/components/files/PieceJointePicker'
import { fichiersColles } from '@/utils/pressePapier'
import { AjoutMachines } from '@/components/issues/AjoutMachines'
import { DocumentationALaDeclaration } from '@/components/issues/DocumentationALaDeclaration'
import { useReferencesEDE } from '@/hooks/useReferencesEDE'
import {
  DOCUMENTATION_VIDE,
  documentationDeclaree,
  type SaisieDocumentation,
} from '@/utils/documentationEDE'
import { useClavierEquipement } from '@/components/issues/ClavierEquipement'
import { equipementDeMaDerniereDeclaration, serieParChiffres, type EquipementRepris } from '@/utils/saisieEquipement'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { identitesDeclarant } from '@/utils/identiteDeclarant'
import { libelleAge } from '@/utils/accueil'
import { libelleMachines } from '@/utils/groupes'
import {
  CATEGORIES_PAR_SUFFIXE,
  PHASES_ORDRE,
  SUJETS,
  cleRapprochement,
  estSujet,
  type CategorieEquipement,
  type Equipement,
  type Issue,
  type Metier,
  type NewIssuePayload,
  type Phase,
  type Sujet,
} from '@/types/issue.types'

const PHASES = PHASES_ORDRE
const DEBOUNCE_RECHERCHE_MS = 150

/**
 * Paramètre d'URL de préremplissage de l'équipement.
 *
 * Alimenté par le bouton « Déclarer un problème » de la fiche équipement :
 * l'opérateur qui consulte un équipement et déclare dans la foulée ne doit pas
 * avoir à retaper un numéro de série qu'il a sous les yeux.
 *
 * Également utilisable dans un lien Power Automate ou un QR code collé sur la
 * machine — d'où le choix d'un paramètre d'URL plutôt que d'un état de
 * navigation, qui ne survivrait ni au copier-coller ni au scan.
 */
const PARAM_EQUIPEMENT = 'equipement'

/** « 2 h » → « il y a 2 h » ; « à l'instant » reste tel quel. */
function ilYa(age: string): string {
  return age.startsWith('à') ? age : `il y a ${age}`
}
const MIN_CHARS_AUTOCOMPLETE = 2

export function NewIssuePage(): JSX.Element {
  const navigate = useNavigate()
  // Arrivée par « + › Déclarer une NC-S › Nouveau problème » (06/10) : une fois
  // le problème enregistré, sa fiche s'ouvre sur le formulaire NC-S.
  const pourNcs = (useLocation().state as EtatDeclarationNcs | null)?.ncs === true
  // Ouverte directement (favori, lien) : pas de page précédente → l'accueil.
  const retour = useRetour('/')
  const [searchParams] = useSearchParams()
  const { show } = useSnackbar()
  const { createIssue, issues } = useIssues()
  // Bascule de rôle (Lot 7) : la déclaration sera signée du compte réel.
  const confirmerEcriture = useConfirmerEcriture()

  // Numéro de série demandé par l'URL, figé au premier rendu : le
  // préremplissage est un point de départ, pas une contrainte — si
  // l'utilisateur change d'équipement, l'URL ne doit pas le ramener en arrière.
  const [numeroSeriePrerempli] = useState(
    () => searchParams.get(PARAM_EQUIPEMENT)?.trim() ?? '',
  )

  // Sans équipement dans l'URL : celui de MA dernière déclaration, si elle a
  // moins de 24 h (19/10). Décidé UNE fois, quand les problèmes arrivent, et
  // seulement si le champ n'a pas encore été touché.
  const { user, isFournisseur, enVueRole } = useCurrentUser()
  const saisieTouchee = useRef(false)
  const [repris, setRepris] = useState<EquipementRepris | null>(null)
  const repriseDecidee = useRef(false)
  useEffect(() => {
    if (repriseDecidee.current || numeroSeriePrerempli || !user || issues.length === 0) return
    repriseDecidee.current = true
    if (saisieTouchee.current) return
    setRepris(equipementDeMaDerniereDeclaration(issues, identitesDeclarant(user), Date.now()))
  }, [issues, user, numeroSeriePrerempli])
  const numeroSerieDepart = numeroSeriePrerempli || repris?.numeroSerie || ''

  // ─── State formulaire ───
  const [metier, setMetier] = useState<Metier | null>(null)
  // Sujet (23/10) : « Goulotte » se choisit avec les métiers et impose le sien
  // (Méca). Tant que les sujets ne sont pas ouverts, l'administrateur seul le voit.
  const [sujet, setSujet] = useState<Sujet | null>(null)
  const sujetsProposes = SUJETS.filter(() => sujetsOuverts() || (!!user && estAdmin(user.upn)))
  const [titre, setTitre] = useState('')
  const [phase, setPhase] = useState<Phase>('Fabrication')
  const [description, setDescription] = useState('')

  // Équipement
  const [equipementInput, setEquipementInput] = useState('')
  const [suggestions, setSuggestions] = useState<Equipement[]>([])
  const [equipementResolu, setEquipementResolu] = useState<Equipement | null>(null)
  const [equipementLoading, setEquipementLoading] = useState(false)
  const [equipementErreur, setEquipementErreur] = useState<string | null>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)

  // N° de plan (MECA) ou EDE (ELEC / AUTOM), facultatifs (15/10). L'EDE déjà
  // connue de la machine (même NVS ou même N° de série) n'est pas redemandée.
  const [documentation, setDocumentation] = useState<SaisieDocumentation>(DOCUMENTATION_VIDE)
  const { refs: refsEDE, cle: cleEDE } = useReferencesEDE(issues)
  const edeConnue = equipementResolu ? refsEDE.has(cleEDE(equipementResolu.numeroSerie)) : false

  // Autres machines touchées par le même défaut (Lot 14)
  const [autresMachines, setAutresMachines] = useState<Equipement[]>([])
  const [ajoutOuvert, setAjoutOuvert] = useState(false)

  // Pièces jointes (multi-fichiers)
  const [fichiers, setFichiers] = useState<FichierEnAttente[]>([])
  const [uploadProgress, setUploadProgress] = useState<string | null>(null)

  const [submitting, setSubmitting] = useState(false)
  const justSelected = useRef(false)
  // Pavé numérique d'office sur téléphone, « ABC » pour les lettres (26/09).
  const champEquipement = useRef<HTMLInputElement>(null)
  const clavier = useClavierEquipement(champEquipement)

  // ─── Catégorie déduite ───
  const categorieDeduite: CategorieEquipement | null = useMemo(
    () =>
      equipementResolu
        ? deduireCategorieEquipement(equipementResolu.numeroSerie)
        : null,
    [equipementResolu],
  )

  // ─── Préremplissage depuis l'URL ───
  //
  // On résout l'équipement plutôt que de recopier la chaîne dans le champ :
  // sans objet Equipement résolu, `canSubmit` reste faux et le formulaire
  // paraîtrait bloqué alors qu'il est visiblement rempli.
  useEffect(() => {
    if (!numeroSerieDepart) return

    // La saisie est affichée AVANT la résolution, et quoi qu'il arrive ensuite :
    // si le référentiel est injoignable, l'opérateur doit voir quel équipement
    // était demandé pour pouvoir réessayer ou corriger, pas un champ vide.
    // `justSelected` neutralise l'autocomplétion sur cette écriture programmatique.
    justSelected.current = true
    setEquipementInput(numeroSerieDepart)

    let annule = false
    void (async () => {
      setEquipementLoading(true)
      try {
        const eq = await getEquipementByNumeroSerie(numeroSerieDepart)
        if (annule) return
        if (eq) {
          setEquipementResolu(eq)
        } else {
          // Référence obsolète (équipement retiré du référentiel).
          setEquipementErreur('Aucun équipement trouvé')
        }
      } catch (err) {
        if (!annule) {
          setEquipementErreur(
            err instanceof Error ? err.message : 'Erreur de recherche',
          )
        }
      } finally {
        if (!annule) setEquipementLoading(false)
      }
    })()

    return () => {
      annule = true
    }
  }, [numeroSerieDepart])

  // ─── Autocomplétion ───
  useEffect(() => {
    if (justSelected.current) {
      justSelected.current = false
      return
    }

    const value = equipementInput.trim()

    if (!value) {
      setSuggestions([])
      setEquipementResolu(null)
      setEquipementErreur(null)
      setShowSuggestions(false)
      return
    }

    if (
      equipementResolu &&
      cleRapprochement(value) === cleRapprochement(equipementResolu.numeroSerie)
    ) {
      setShowSuggestions(false)
      return
    }

    if (value.length < MIN_CHARS_AUTOCOMPLETE) {
      setSuggestions([])
      setEquipementResolu(null)
      setEquipementErreur(null)
      setShowSuggestions(false)
      return
    }

    let cancelled = false
    const t = setTimeout(async () => {
      setEquipementLoading(true)
      setEquipementErreur(null)
      try {
        const results = await searchEquipements(value)
        if (cancelled) return

        setSuggestions(results)
        setShowSuggestions(true)

        const exactMatch = results.find(
          (eq) => cleRapprochement(eq.numeroSerie) === cleRapprochement(value),
        )
        // Au pavé numérique : « 4825 » suffit quand une seule machine porte ce
        // numéro — le champ se complète en « 4825-SC ».
        const parChiffres = exactMatch ? null : serieParChiffres(results, value)
        if (parChiffres) {
          justSelected.current = true
          setEquipementInput(parChiffres.numeroSerie)
          setEquipementResolu(parChiffres)
          setSuggestions([])
          setShowSuggestions(false)
        } else if (exactMatch) {
          setEquipementResolu(exactMatch)
        } else if (results.length === 0) {
          setEquipementResolu(null)
          setEquipementErreur('Aucun équipement trouvé')
        } else {
          setEquipementResolu(null)
        }
      } catch (err) {
        if (!cancelled) {
          setEquipementErreur(err instanceof Error ? err.message : 'Erreur de recherche')
        }
      } finally {
        if (!cancelled) setEquipementLoading(false)
      }
    }, DEBOUNCE_RECHERCHE_MS)

    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [equipementInput, equipementResolu])

  /** « Changer » sous l'équipement repris : champ vidé, curseur dedans. */
  function changerEquipementRepris(): void {
    saisieTouchee.current = true
    setRepris(null)
    setEquipementInput('')
    setEquipementResolu(null)
    setEquipementErreur(null)
    champEquipement.current?.focus()
  }

  function selectEquipement(eq: Equipement): void {
    saisieTouchee.current = true
    justSelected.current = true
    setEquipementInput(eq.numeroSerie)
    setEquipementResolu(eq)
    setSuggestions([])
    setShowSuggestions(false)
    setEquipementErreur(null)
  }

  // ─── Validation ───
  const canSubmit =
    metier !== null &&
    titre.trim() !== '' &&
    equipementResolu !== null &&
    categorieDeduite !== null

  // ─── Soumission ───
  async function handleSubmit(): Promise<void> {
    if (!canSubmit || !metier || !equipementResolu || !categorieDeduite) return
    const groupe = autresMachines.length > 0
    if (!confirmerEcriture(groupe ? `Déclarer ce problème sur ${libelleMachines(autresMachines.length + 1)}` : 'Déclarer ce problème')) return
    setSubmitting(true)
    try {
      const payload: NewIssuePayload = {
        titre: titre.trim(),
        metier,
        ...(sujet ? { sujet } : {}),
        phase,
        numeroSerie: equipementResolu.numeroSerie,
        of: equipementResolu.of,
        pn: equipementResolu.pn,
        familleEquipement: categorieDeduite,
        description: description.trim(),
        // Saisie hors format : le message dit quoi corriger, rien n'est créé.
        ...documentationDeclaree(metier, documentation, edeConnue),
      }

      // Abonnement d'office à sa déclaration (29/10) : le réglage est lu pendant
      // la création. Pas pour un fournisseur (pas de cloche), ni en bascule de
      // rôle (on abonnerait la personne affichée sous le compte réel).
      const abonnement =
        user && !isFournisseur && !enVueRole
          ? lireReglageAbonnementsDe(user.upn).then((r) => r.mesDeclarations).catch(() => true)
          : Promise.resolve(false)

      const created = await createIssue(payload, { ouvreGroupe: groupe })
      const creees: Issue[] = [created]

      // Une fiche par autre machine, reliée à la première. Un échec n'annule
      // pas ce qui est créé : il est dit, la machine s'ajoute depuis la fiche.
      const soeurs: string[] = []
      const manquees: string[] = []
      for (const eq of autresMachines) {
        // Machine de départ changée entre-temps pour l'une des « autres » : pas de doublon.
        if (cleRapprochement(eq.numeroSerie) === cleRapprochement(equipementResolu.numeroSerie)) continue
        const categorie = deduireCategorieEquipement(eq.numeroSerie)
        if (!categorie) {
          manquees.push(eq.numeroSerie)
          continue
        }
        setUploadProgress(`Fiche ${soeurs.length + manquees.length + 2}/${autresMachines.length + 1}…`)
        try {
          const soeur = await createIssue({
            ...payload,
            numeroSerie: eq.numeroSerie,
            of: eq.of,
            pn: eq.pn,
            familleEquipement: categorie,
            groupe: created.id,
          })
          soeurs.push(soeur.id)
          creees.push(soeur)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`[NewIssue] fiche non créée pour ${eq.numeroSerie} :`, err)
          manquees.push(eq.numeroSerie)
        }
      }
      if (manquees.length > 0) {
        show(
          `${created.id} déclaré, mais pas sur ${manquees.join(', ')} : ajoutez-${manquees.length > 1 ? 'les' : 'la'} depuis la fiche`,
          'error',
        )
      }

      // Abonnement : en même temps que les pièces jointes, au mieux — un échec
      // ne touche pas la déclaration, la cloche de la fiche reste là.
      const abonner = abonnement.then((oui) => {
        if (!oui || !user) return
        return Promise.allSettled(
          creees.map((i) => suivreIssueParId(i.id, user.upn, true)),
        ).then((res) => {
          const echecs = res.filter((r) => r.status === 'rejected')
          // eslint-disable-next-line no-console
          if (echecs.length) console.warn('[NewIssue] abonnement non enregistré :', echecs)
        })
      })

      // Upload des pièces jointes — best effort, ne bloque pas la déclaration
      if (fichiers.length > 0) {
        setUploadProgress(`Upload des pièces jointes (0/${fichiers.length})…`)
        try {
          const results = await envoyerFichiers(
            created.id,
            fichiers,
            (fileIndex) => {
              setUploadProgress(
                `Upload des pièces jointes (${fileIndex + 1}/${fichiers.length})…`,
              )
            },
          )

          const echecs = results.filter((r) => !r.success)
          if (echecs.length > 0) {
            show(
              `${echecs.length} pièce(s) jointe(s) non uploadée(s)`,
              'error',
            )
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[NewIssue] Upload PJ échoué:', err)
          show('Problème déclaré, mais certains fichiers n\'ont pas pu être uploadés', 'error')
        }
      }

      await abonner

      // Mentions « @ » de la description (02/11) : une notification, sur la
      // première fiche ; pas pour qui reçoit déjà l'alerte du flow 1.
      const bilanMentions =
        user && !isFournisseur && payload.description.includes('@')
          ? await notifierMentions({
              issue: created,
              geste: 'declaration',
              texte: payload.description,
              auteur: identitesDeclarant(user),
              ...(equipementResolu.familleEquipement ? { modele: equipementResolu.familleEquipement } : {}),
            })
          : null
      const mentions = bilanMentions ? resumeMentions(bilanMentions.prevenus, bilanMentions.echec, bilanMentions.essai) : ''

      if (manquees.length === 0) {
        show(
          (soeurs.length > 0
            ? `Problème déclaré sur ${libelleMachines(soeurs.length + 1)} : ${[created.id, ...soeurs].join(', ')}`
            : `Problème ${created.id} déclaré`) + mentions,
          'success',
        )
      }
      if (pourNcs) {
        const etat: EtatModale = { ouvrirNcs: true }
        navigate(lienProbleme(created.id), { replace: true, state: etat })
      } else {
        navigate('/issues', { replace: true })
      }
    } catch (err) {
      show(err instanceof Error ? err.message : 'Erreur de déclaration', 'error')
    } finally {
      setSubmitting(false)
      setUploadProgress(null)
    }
  }

  const header = (
    <Header
      level={3}
      title={pourNcs ? 'NC-S · le problème' : 'Nouveau problème'}
      showBack
      onBack={retour}
      rightAction={
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label="Fermer"
          className="p-2 -mr-2"
          disabled={submitting}
        >
          <X size={22} />
        </button>
      }
    />
  )

  return (
    <AppShell header={header} hideBottomNav>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        className="pb-28"
      >
        {pourNcs && (
          <p
            className="flex items-start gap-2"
            style={{ padding: '10px 12px', borderRadius: 8, backgroundColor: 'var(--color-gray-5)', fontSize: 14, lineHeight: '20px', margin: '16px 16px 0' }}
          >
            <InfoIcon size={18} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
            <span>
              <strong>Étape 1 sur 2 : le problème</strong> — équipement, description, photos. Le formulaire NC-S
              s&apos;ouvrira ensuite, pré-rempli.
            </span>
          </p>
        )}
        {/* 1. Métier */}
        <FormSection title="Métier *">
          {/* Les cinq visibles d'un coup, Goulotte à côté de MECA (23/10) : la
              rangée passe à la ligne au lieu de défiler hors de l'écran. */}
          <div className="flex flex-wrap gap-2">
            {rangeeMetiersEtSujets(METIERS_ORDRE, sujetsProposes).map((cle) => {
              const x = estSujet(cle) ? cle : null
              const m = x ? SUJETS_CONFIG[x].metier : (cle as Metier)
              const choisi = x ? sujet === x : !sujet && metier === m
              return (
                <button
                  key={cle}
                  type="button"
                  onClick={() => {
                    setMetier(m)
                    setSujet(x)
                  }}
                  className="flex-shrink-0 px-4 py-2 rounded-full"
                  style={{
                    // ⚠️ Non sélectionnée, la puce était en gris 5 — exactement
                    // la teinte du plan : elle disparaissait. Surface blanche
                    // bordée, comme les champs de saisie juste en dessous.
                    backgroundColor: choisi ? METIERS_COULEURS[m] : 'var(--color-bg-card)',
                    color: choisi ? METIERS_COULEURS_TEXTE[m] : 'var(--color-text-primary)',
                    // Bordure toujours présente, transparente quand la puce est
                    // pleine : les deux états gardent la même hauteur.
                    border: choisi ? '1px solid transparent' : '1px solid var(--color-border)',
                    fontSize: 'var(--font-size-copy2)',
                    fontWeight: choisi ? 500 : 400,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {x ? SUJETS_CONFIG[x].libelleCourt : METIERS_LABELS_COURT[m]}
                </button>
              )
            })}
          </div>
          {sujet && (
            <p style={{ margin: '8px 0 0', fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}>
              {SUJETS_CONFIG[sujet].aide}
            </p>
          )}
        </FormSection>

        {/* 2. Titre */}
        <FormSection title="Titre *">
          <input
            type="text"
            value={titre}
            onChange={(e) => setTitre(e.target.value.slice(0, 120))}
            placeholder="Résumé du problème…"
            className="w-full h-12 px-3 rounded-lg outline-none"
            style={{
              backgroundColor: 'var(--color-white)',
              border: '1px solid var(--color-border)',
              fontSize: 'var(--font-size-copy2)',
            }}
          />
          <p
            className="text-right mt-1"
            style={{
              fontSize: 'var(--font-size-info)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {titre.length}/120
          </p>
        </FormSection>

        {/* 3 + 4. Équipement et Phase ───
            Appariés au-delà de 768px. Sur la colonne de 760px, un select de
            cinq valeurs courtes occupait toute la largeur : l'œil parcourait
            700px de vide entre le libellé et la valeur. Le titre et la
            description restent pleine largeur, ce sont eux qu'on saisit
            vraiment. */}
        <div className="form-grid form-grid-2 px-4 py-3">
        <FormSection title="Équipement *" dansGrille>
          <div className="relative">
            <div className="relative">
              <input
                ref={champEquipement}
                type="text"
                {...clavier.proprietes}
                value={equipementInput}
                onChange={(e) => {
                  saisieTouchee.current = true
                  setEquipementInput(e.target.value)
                  setEquipementResolu(null)
                }}
                placeholder="N° série, OF, PN ou CVE…"
                className={`w-full h-12 pl-10 ${clavier.bascule ? 'pr-20' : 'pr-3'} rounded-lg outline-none`}
                style={{
                  backgroundColor: 'var(--color-white)',
                  border: '1px solid var(--color-border)',
                  fontSize: 'var(--font-size-copy2)',
                }}
              />
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                color="var(--color-gray-50)"
              />
              {clavier.bascule && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2">{clavier.bascule}</span>
              )}
            </div>

            {showSuggestions && suggestions.length > 0 && (
              <div
                className="absolute z-10 w-full mt-1 rounded-lg overflow-hidden"
                style={{
                  backgroundColor: 'var(--color-white)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  border: '1px solid var(--color-border)',
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                {suggestions.map((eq) => (
                  <button
                    key={eq.numeroSerie}
                    type="button"
                    onClick={() => selectEquipement(eq)}
                    className="w-full text-left px-3 py-2.5"
                    style={{
                      fontSize: 'var(--font-size-copy2)',
                      borderBottom: '1px solid var(--color-border)',
                      backgroundColor: 'transparent',
                      display: 'block',
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>{eq.numeroSerie}</span>
                    <span
                      style={{
                        fontSize: 'var(--font-size-info)',
                        color: 'var(--color-text-secondary)',
                        marginLeft: 8,
                      }}
                    >
                      {eq.pn} · {eq.of}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {equipementLoading && (
            <p
              className="mt-2"
              style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}
            >
              Recherche…
            </p>
          )}

          {equipementResolu && (
            <div
              className="mt-2 p-3 rounded-lg"
              style={{
                backgroundColor: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
              }}
            >
              <div className="flex items-baseline justify-between" style={{ gap: 8, marginBottom: 4 }}>
                <p style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)', margin: 0 }}>
                  {repris && cleRapprochement(repris.numeroSerie) === cleRapprochement(equipementResolu.numeroSerie)
                    ? `Repris de votre dernière déclaration (${ilYa(libelleAge(repris.le, Date.now()))})`
                    : 'Équipement identifié'}
                </p>
                {repris && cleRapprochement(repris.numeroSerie) === cleRapprochement(equipementResolu.numeroSerie) && (
                  <button
                    type="button"
                    onClick={changerEquipementRepris}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: '4px 0',
                      fontFamily: 'inherit',
                      fontSize: 'var(--font-size-info)',
                      fontWeight: 500,
                      color: 'var(--color-text-primary)',
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    Changer
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Info label="N° série" value={equipementResolu.numeroSerie} />
                <Info label="OF" value={equipementResolu.of} />
                <Info label="PN" value={equipementResolu.pn} />
              </div>
              {equipementResolu.familleEquipement && (
                <p style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)', marginTop: 6 }}>
                  {equipementResolu.familleEquipement}
                  {equipementResolu.designation ? ` — ${equipementResolu.designation}` : ''}
                </p>
              )}
              {categorieDeduite && (
                <p style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                  Catégorie : {categorieDeduite}
                </p>
              )}
              {!categorieDeduite && (
                <p style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-error)', marginTop: 2 }}>
                  ⚠️ Suffixe N° de série non reconnu (
                  {Object.keys(CATEGORIES_PAR_SUFFIXE)
                    .map((s) => `-${s}`)
                    .join(', ')}{' '}
                  attendus)
                </p>
              )}
              {equipementResolu.cve && (
                <p style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                  CVE : {equipementResolu.cve}
                </p>
              )}
            </div>
          )}

          {equipementErreur && !equipementLoading && !equipementResolu && (
            <p
              className="mt-2"
              style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-error)' }}
            >
              {equipementErreur}
            </p>
          )}
        </FormSection>

        <FormSection title="Phase *" dansGrille>
          <select
            value={phase}
            onChange={(e) => setPhase(e.target.value as Phase)}
            className="w-full h-12 px-3 rounded-lg outline-none"
            style={{
              backgroundColor: 'var(--color-white)',
              border: '1px solid var(--color-border)',
              fontSize: 'var(--font-size-copy2)',
            }}
          >
            {PHASES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </FormSection>
        </div>

        {/* Même défaut sur d'autres machines (Lot 14) : une fiche par
            machine. Replié par défaut — c'est rare (« mais ça arrive »). */}
        {equipementResolu && categorieDeduite && (
          <section className="px-4 pb-3">
            {autresMachines.length > 0 && (
              <>
                <h3 style={{ fontSize: 'var(--font-size-copy2)', fontWeight: 500, marginBottom: 8 }}>
                  Même défaut sur {libelleMachines(autresMachines.length)} de plus
                </h3>
                <div className="flex flex-wrap gap-2" style={{ marginBottom: 8 }}>
                  {autresMachines.map((eq) => (
                    <span
                      key={eq.numeroSerie}
                      className="flex items-center gap-1"
                      style={{
                        minHeight: 40,
                        padding: '0 4px 0 12px',
                        borderRadius: 20,
                        backgroundColor: 'var(--color-gray-5)',
                        border: '1px solid var(--color-border)',
                        fontSize: 'var(--font-size-copy2)',
                      }}
                    >
                      {eq.numeroSerie}
                      {eq.pn !== equipementResolu.pn && (
                        <span style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}>
                          · {eq.pn}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setAutresMachines((l) => l.filter((x) => x.numeroSerie !== eq.numeroSerie))}
                        aria-label={`Retirer ${eq.numeroSerie}`}
                        disabled={submitting}
                        className="flex items-center justify-center"
                        style={{ width: 32, height: 32, border: 'none', background: 'transparent' }}
                      >
                        <X size={16} color="var(--color-gray-70)" />
                      </button>
                    </span>
                  ))}
                </div>
              </>
            )}
            {ajoutOuvert ? (
              <AjoutMachines
                pn={equipementResolu.pn}
                exclus={[equipementResolu.numeroSerie, ...autresMachines.map((e) => e.numeroSerie)]}
                onAjouter={(eq) => setAutresMachines((l) => [...l, eq])}
                desactive={submitting}
              />
            ) : (
              <button
                type="button"
                onClick={() => setAjoutOuvert(true)}
                disabled={submitting}
                className="flex items-center gap-2"
                style={{
                  minHeight: 44,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--color-black)',
                  fontSize: 'var(--font-size-copy2)',
                  textDecoration: 'underline',
                }}
              >
                <Plus size={16} />
                Même défaut sur d&apos;autres équipements ?
              </button>
            )}
            {autresMachines.length > 0 && (
              <p style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)', margin: '8px 0 0' }}>
                Une fiche par équipement, reliées entre elles. Les photos sont déposées une fois et
                visibles sur toutes les fiches.
              </p>
            )}
          </section>
        )}

        {/* 5. Description */}
        <FormSection title="Description">
          <ZoneMentions
            value={description}
            onValeur={(t) => setDescription(t.slice(0, 500))}
            sansMentions={isFournisseur}
            // Une capture ou un fichier collé ici rejoint les pièces jointes.
            onPaste={(e) => {
              const colles = fichiersColles(e.clipboardData)
              if (colles.length === 0 || submitting) return
              e.preventDefault()
              const { nouveaux, erreurs } = preparerFichiers(colles)
              if (nouveaux.length > 0) {
                setFichiers((prev) => [...prev, ...nouveaux])
                show(nouveaux.length > 1 ? `${nouveaux.length} fichiers ajoutés aux pièces jointes` : 'Fichier ajouté aux pièces jointes', 'success')
              }
              if (erreurs.length > 0) show(erreurs.join(' '), 'error')
            }}
            placeholder={isFournisseur ? 'Détails, contexte, observations…' : 'Détails, contexte, observations… (@ pour mentionner quelqu’un)'}
            rows={4}
            className="w-full p-3 rounded-lg outline-none resize-none"
            style={{
              backgroundColor: 'var(--color-white)',
              border: '1px solid var(--color-border)',
              fontSize: 'var(--font-size-copy2)',
            }}
          />
          <p
            className="text-right mt-1"
            style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}
          >
            {description.length}/500
          </p>
        </FormSection>

        {/* Documentation connue d'emblée : lien discret, selon le métier choisi */}
        <DocumentationALaDeclaration
          metier={metier}
          edeConnue={edeConnue}
          valeur={documentation}
          onChange={setDocumentation}
          desactive={submitting}
        />

        {/* 6. Pièces jointes */}
        <FormSection title="Photos et fichiers joints">
          <PieceJointePicker
            fichiers={fichiers}
            onChange={setFichiers}
            disabled={submitting}
          />
        </FormSection>

        {/* Note info coordinateur */}
        <section className="px-4 pb-4">
          <div
            className="flex gap-2 p-3 rounded-lg"
            style={{
              backgroundColor: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
            }}
          >
            <InfoIcon
              size={16}
              color="var(--color-gray-60)"
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <p
              style={{
                fontSize: 'var(--font-size-info)',
                lineHeight: 'var(--line-height-info)',
                color: 'var(--color-text-secondary)',
                margin: 0,
              }}
            >
              La famille et la criticité seront renseignées par le coordinateur
              métier après vérification.
            </p>
          </div>
        </section>
      </form>

      {/* ─── Barre d'action collée en bas ───
          Le bandeau reste pleine largeur (c'est une barre, elle doit toucher
          les bords), mais son contenu est recadré sur la même colonne de 760px
          que le formulaire. Sans ça, sur un écran de bureau, le bouton
          s'étirait sur toute la fenêtre pendant que les champs restaient
          centrés au milieu : les deux ne semblaient plus appartenir à la même
          page. */}
      <div
        className="fixed left-0 right-0 bottom-0"
        style={{
          backgroundColor: 'var(--color-white)',
          borderTop: '1px solid var(--color-border)',
          boxShadow: '0 -2px 12px rgba(0, 0, 0, 0.08)',
        }}
      >
      <div
        className="p-4"
        style={{
          maxWidth: 'var(--width-content)',
          marginInline: 'auto',
          paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0))',
        }}
      >
        {uploadProgress && (
          <p
            style={{
              fontSize: 'var(--font-size-info)',
              color: 'var(--color-text-secondary)',
              textAlign: 'center',
              marginBottom: 8,
            }}
          >
            {uploadProgress}
          </p>
        )}
        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={!canSubmit || submitting}
          onClick={() => void handleSubmit()}
        >
          {submitting
            ? uploadProgress
              ? 'Upload en cours…'
              : 'Envoi en cours…'
            : `Déclarer${autresMachines.length > 0 ? ` sur ${libelleMachines(autresMachines.length + 1)}` : ''}${fichiers.length > 0 ? ` (${fichiers.length} fichier${fichiers.length > 1 ? 's' : ''})` : ''}`}
        </Button>
      </div>
      </div>
    </AppShell>
  )
}

// ─── Sous-composants ───

function FormSection({
  title,
  children,
  dansGrille,
}: {
  title: string
  children: React.ReactNode
  /**
   * Supprime les marges propres à la section.
   *
   * En cellule de `.form-grid-2`, l'espacement est porté par le conteneur :
   * le laisser ici le compterait deux fois et creuserait un couloir vide entre
   * les deux colonnes.
   */
  dansGrille?: boolean
}): JSX.Element {
  return (
    <section className={dansGrille ? undefined : 'px-4 py-3'}>
      <h3
        style={{
          fontSize: 'var(--font-size-copy2)',
          fontWeight: 500,
          marginBottom: 8,
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  )
}

function Info({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <p style={{ fontSize: 10, color: 'var(--color-text-secondary)' }}>{label}</p>
      <p style={{ fontSize: 'var(--font-size-copy2)', fontWeight: 500 }}>{value || '—'}</p>
    </div>
  )
}
